import * as React from "react";
import {
  formatPhone,
  formatPhoneInput,
  phoneCaretPosition,
} from "@workspace/phone-format";

type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "defaultValue" | "onChange" | "onBlur"
> & {
  value?: string | null;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Receives the native input event in addition to onValueChange. */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
};

/**
 * Public-site counterpart to the dashboard phone field. It supports both
 * controlled and uncontrolled forms, retaining caret position while the US
 * punctuation is inserted.
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value,
      defaultValue = "",
      onValueChange,
      onChange,
      onBlur,
      className,
      ...props
    },
    forwardedRef,
  ) => {
    const controlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const pendingCaretRef = React.useRef<number | null>(null);
    const currentValue = controlled ? (value ?? "") : internalValue;
    const displayValue = formatPhoneInput(currentValue);

    const setInputRef = React.useCallback(
      (element: HTMLInputElement | null) => {
        inputRef.current = element;
        if (typeof forwardedRef === "function") {
          forwardedRef(element);
        } else if (forwardedRef) {
          forwardedRef.current = element;
        }
      },
      [forwardedRef],
    );

    React.useLayoutEffect(() => {
      const input = inputRef.current;
      const caret = pendingCaretRef.current;
      if (!input || caret === null) return;
      if (document.activeElement !== input) {
        pendingCaretRef.current = null;
        return;
      }
      input.setSelectionRange(caret, caret);
      pendingCaretRef.current = null;
    }, [displayValue]);

    return (
      <input
        {...props}
        ref={setInputRef}
        type="tel"
        className={className}
        value={displayValue}
        onChange={(event) => {
          const rawValue = event.currentTarget.value;
          const rawCaret =
            event.currentTarget.selectionStart ?? rawValue.length;
          const nextValue = formatPhoneInput(rawValue);
          pendingCaretRef.current = phoneCaretPosition(
            rawValue,
            nextValue,
            rawCaret,
          );
          if (!controlled) setInternalValue(nextValue);
          onValueChange?.(nextValue);
           onChange?.(event);
        }}
        onBlur={(event) => {
          const nextValue = formatPhone(event.currentTarget.value);
          if (!controlled && nextValue !== event.currentTarget.value) {
            setInternalValue(nextValue);
          }
          if (controlled && nextValue !== currentValue) {
            onValueChange?.(nextValue);
          }
          onBlur?.(event);
        }}
      />
    );
  },
);

PhoneInput.displayName = "PhoneInput";